import * as THREE from '@phdsoft/three';
import type { ElementMapEntry } from '../digital-twin/graphics/lightsection-mask';

/**
 * Names under which a merged lightsection mesh carries its per-vertex element index (the conversor
 * writes `_EL_IDX`, GLTFLoader exposes it as `__el_idx`). Copy of `EL_IDX_SOURCE_NAMES` in
 * `digital-twin/graphics/lightsection-mask.ts` (not exported there); keep both lists in sync.
 */
export const EL_IDX_ATTRIBUTE_NAMES: readonly string[] = ['__el_idx', '_EL_IDX', '_el_idx', '__EL_IDX'];

/** Structure element behind a raycast hit: its Guid and, when known, its section Guid. */
export interface ElementRef { elementId: string; sectionId: string | null; }

/** Guid without dashes, lower case (the `elementMap` ids may carry dashes, element Guids may not). */
function normalizeGuid(id: string): string {
  return id.replace(/-/g, '').toLowerCase();
}

/** True when both ids name the same Guid, ignoring dashes and case. */
export function sameGuid(a: string | null | undefined, b: string | null | undefined): boolean {
  return !!a && !!b && normalizeGuid(a) === normalizeGuid(b);
}

/**
 * Spellings of `id` to look up exactly (`Section.getElementById`): as given, without dashes, and the
 * 8-4-4-4-12 dashed form of a 32-hex-character id. No duplicates.
 */
export function guidVariants(id: string): string[] {
  const variants = [id, id.replace(/-/g, '')];
  if (/^[0-9a-f]{32}$/i.test(variants[1])) {
    const u = variants[1];
    variants.push(`${u.slice(0, 8)}-${u.slice(8, 12)}-${u.slice(12, 16)}-${u.slice(16, 20)}-${u.slice(20)}`);
  }
  return [...new Set(variants)];
}

function elementIndexAttribute(geometry: THREE.BufferGeometry | undefined): THREE.BufferAttribute | THREE.InterleavedBufferAttribute | null {
  if (!geometry?.getAttribute) return null;
  for (const name of EL_IDX_ATTRIBUTE_NAMES) {
    const attr = geometry.getAttribute(name);
    if (attr) return attr;
  }
  return null;
}

/**
 * Section Guid of `obj`: the first object up the parent chain (itself included) with a
 * `userData.SectionID`, or whose `name` is one of `candidates` (dashes/case ignored; the candidate's
 * own spelling is returned). Mirrors dev's `findRegionAndSectionIds` (userData, then parent chain).
 */
function sectionIdFromAncestors(obj: THREE.Object3D, candidates: Map<string, string>): string | null {
  for (let o: THREE.Object3D | null = obj; o; o = o.parent) {
    const fromUserData = o.userData?.SectionID;
    if (typeof fromUserData === 'string' && fromUserData) return fromUserData;
    const fromName = o.name ? candidates.get(normalizeGuid(o.name)) : undefined;
    if (fromName) return fromName;
  }
  return null;
}

/**
 * Element id + section id of a hit on a structure mesh, for the three new-GLTF mesh kinds:
 * - merged lightsection mesh: the element index of the hit face's first vertex, looked up in
 *   `userData.elementMap` (unknown index: null, never the merged mesh's own name);
 * - lightsection overlay (custom material / repair mod): `userData.elementId`;
 * - detailed element mesh (also the old format): `userData.Guid`, else `name` (= element Guid).
 * The section comes from the elementMap entry, else from `sectionIdFromAncestors`.
 */
export function elementRefFromHit(hit: THREE.Intersection | null | undefined, candidateSectionGuids?: Iterable<string>): ElementRef | null {
  const obj = hit?.object;
  if (!obj) return null;
  const candidates = new Map<string, string>();
  for (const guid of candidateSectionGuids ?? []) if (guid) candidates.set(normalizeGuid(guid), guid);
  const sectionOf = () => sectionIdFromAncestors(obj, candidates);

  const elementMap = obj.userData?.elementMap as ElementMapEntry[] | undefined;
  const indexAttr = Array.isArray(elementMap) ? elementIndexAttribute((obj as THREE.Mesh).geometry as THREE.BufferGeometry) : null;
  if (indexAttr) {
    if (hit.face?.a === undefined || hit.face.a === null) return null;
    const entry = elementMap[Math.round(indexAttr.getX(hit.face.a))];
    if (!entry?.elementId) return null;
    return { elementId: entry.elementId, sectionId: entry.sectionId ?? sectionOf() };
  }

  const overlayId = obj.userData?.elementId;
  if (typeof overlayId === 'string' && overlayId) return { elementId: overlayId, sectionId: sectionOf() };

  const id = obj.userData?.Guid ?? obj.name;
  if (typeof id !== 'string' || !id) return null;
  return { elementId: id, sectionId: sectionOf() };
}
